// Applies a single accepted member review update (an HRIS/app "create" or
// "update") and reports whether the write actually succeeded.
//
// This exists as a standalone, dependency-injected unit because the original
// inline version in members.vue (runCommandReviewUpdates) flipped the review
// doc to "success" before — and regardless of whether — the write happened:
// the duplicate/archived-email reads ran in fire-and-forget firebase
// `.once('value', cb)` callbacks whose closure `error` flag was checked
// synchronously, and the create/update dispatches were not awaited. The
// consequence was that accepting a create for an already-registered email, or
// hitting a network failure mid-create, consumed the review item with nothing
// written. Sequencing and awaiting the reads and writes here, and deriving the
// resulting status from what actually happened, is what fixes that.
export interface ReviewUpdateApplyDeps {
  // Resolves true when /users/{emailKey} already exists (email taken).
  isEmailRegistered: (emailKey: string) => Promise<boolean>
  // Resolves true when /users_employee_exit/{emailKey} exists (archived).
  isEmailArchived: (emailKey: string) => Promise<boolean>
  // Performs the actual create write; rejects on failure.
  createMember: () => Promise<any>
  // Performs the actual update write; rejects on failure.
  updateMember: () => Promise<any>
}

export interface ReviewUpdateApplyResult {
  // True only when the intended write completed successfully.
  ok: boolean
  // Status to persist on the review doc: 'success' iff the write landed.
  status: 'success' | 'failed'
  // User-facing reason when ok is false.
  errorMsg?: string
}

export async function applyMemberReviewUpdate(
  opts: { emailKey: string; mode: 'edit' | 'create' },
  deps: ReviewUpdateApplyDeps
): Promise<ReviewUpdateApplyResult> {
  try {
    if (opts.mode === 'edit') {
      await deps.updateMember()
      return { ok: true, status: 'success' }
    }

    // Create path: guard against re-using an already-registered or archived
    // email before writing. Both reads must be awaited so their result is
    // known before we decide to create.
    if (await deps.isEmailRegistered(opts.emailKey)) {
      return {
        ok: false,
        status: 'failed',
        errorMsg: 'This email has already been registered.',
      }
    }

    if (await deps.isEmailArchived(opts.emailKey)) {
      return {
        ok: false,
        status: 'failed',
        errorMsg: 'The email is already in use (as an archived member)',
      }
    }

    await deps.createMember()
    return { ok: true, status: 'success' }
  } catch (e: any) {
    return {
      ok: false,
      status: 'failed',
      errorMsg: (e && e.message) || 'Failed to apply the update. Please try again.',
    }
  }
}
